import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { useFormik } from "formik";
import * as Yup from "yup";
import { EditableColumnDef, EditableTable } from "../../../../../../shared/components/table/inline-edit-table";
import { CustomSelect, CustomTextField } from "../../../../../../shared/components/inputfields/form-elements";
import { StyledButton } from "../../../../../../shared/components/buttons/custom-button";
import { StandardDetails } from "../../../sc-models/scope-certificate-details-dto";
import { OptionType } from "../../../../../../shared/components/form-builder/form-config.model";
import useNotification from "../../../../../../shared/hooks/useNotification";
import { useParams } from "react-router-dom";
import { useScStore } from "../../../../../../store/scStandard.store";
import { useTransactionCertificateStore } from "../../../../../../store/transaction-certificates.store";
import { dtrackItServiceV4 } from "../../../sc-service/dtrackit.service";
import useScrollToError from "../../../../../../shared/hooks/useScrollToError";


interface StandardDetailsFormProps {
  standardData: StandardDetails[];
  setStandardData: React.Dispatch<React.SetStateAction<StandardDetails[]>>;
  scStatus?: string;
  ccsFlag?: string;
  scValidFromDate?: string;
  scWithdrawalDate?: string;
  isSuspended?: boolean;
  isFarmSC?: string;
  isLocked?: boolean;
}

const StandardDetailsForm: React.FC<StandardDetailsFormProps> = ({
  standardData,
  setStandardData,
  scStatus,
  ccsFlag,
  scValidFromDate,
  scWithdrawalDate,
  isSuspended,
  isFarmSC,
  isLocked,
}) => {
  const { formtype } = useParams();
  const hasSubmitSC: boolean = formtype === 'submited-sc';
  const [data, setData] = useState<StandardDetails[]>(standardData);
  const [tableData, setTableData] = useState<StandardDetails[]>(standardData);
  const [editingRowIndex, setEditingRowIndex] = useState<number>(-1);
  const [withdrawalReasonOptions, setWithdrawalReasonOptions] = useState<OptionType[]>([]);
  const [scStandardOptions, setScStandardOptions] = useState<OptionType[]>([]);
  const [standardVersionOptions, setStandardVersionOptions] = useState<OptionType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const { notify } = useNotification();
  const { setScStandards, setScStandardDescriptions } = useScStore();
  const { setRestrictPopUpSave, isRestrictPopUpSave } = useTransactionCertificateStore();

  const isExpired = scStatus === 'E';
  const isWithdrawn = scStatus === 'W';
  const isEditingDisabled = hasSubmitSC || isExpired || isLocked; // Lock if submitted, expired, OR tab is locked

  // Dynamic validation schema based on SC Status
  const getValidationSchema = (scStatus?: string, scWithdrawalDate?: string) => {
    return Yup.object().shape({
      scStandard: Yup.string().required("SC Standard is required"),
      scStandardDescription: Yup.string(),
      scStandardVersion: Yup.string().required("SC Standard Version is required"),
      scStandardAddedDate: Yup.string()
        .required("SC Standard Added Date is required")
        // Cross-field: Added Date must be on or after SC Valid From Date
        .test('standard-added-date-sequence', 'SC Standard Added Date cannot be before SC Valid From Date',
          function(value) {
            if (!value || !scValidFromDate) return true;
            return new Date(value) >= new Date(scValidFromDate);
          }),
      scStandardRemovedDate: Yup.string()
        .when([], {
          is: () => scStatus === 'W' || !!scWithdrawalDate,
          then: (schema) => schema.required("SC Standard Removed Date is required when SC Status is Withdrawn or Withdrawal Date is filled"),
          otherwise: (schema) => schema.optional()
        }),
      scStandardWithdrawalReason: Yup.array()
        .when(['scStandardRemovedDate'], (scStandardRemovedDate, schema) => {
          if (scStatus === 'W' || !!scWithdrawalDate || (scStandardRemovedDate && scStandardRemovedDate[0])) {
            return schema.min(1, "SC Standard Withdrawal Reason is required");
          }
          return schema.optional();
        }),

    });
  };

  const validationSchema = getValidationSchema(scStatus, scWithdrawalDate);

  // Hardcoded values replaced with API call - see getScStandardValues function below

  // Hardcoded withdrawal reason values - replaced with DTrackIt API
  // const standardWithdrawalReasonValues: OptionType[] = [
  //   { label: "Critical non-conformity", value: "Critical non-conformity", code: "C" },
  //   { label: "Non-conformity", value: "Non-conformity", code: "N" },
  //   { label: "Recertification completed", value: "Recertification completed", code: "R" },
  //   { label: "Withdrawal of the certification body's accreditation", value: "Withdrawal of the certification body's accreditation", code: "W" },
  //   { label: "Administrative", value: "Administrative", code: "A" },
  //   { label: "Voluntary", value: "Voluntary", code: "V" },
  //   { label: "Error", value: "Error", code: "E" },
  //   { label: "Other", value: "Other", code: "O" },
  // ];

  // Fetch SC standard values from DTrackIt API
  const getScStandardValues = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getScStandardValues();
      if (response?.data) {
        let EXCLUDED_STANDARDS = ['SFA', 'GOTS', 'CCS'];
        
        // Exclude farm standards if the user selected "No" for "Is the Scope Certificate for a Farm?"
        if (isFarmSC === 'N') {
          EXCLUDED_STANDARDS = [...EXCLUDED_STANDARDS, 'RWS', 'RMS', 'RAS', 'RDS'];
        }

        const transformedOptions = response.data
          .filter((item: any) => !EXCLUDED_STANDARDS.includes(item.code_1))
          .map((item: any) => ({
            label: `${item.description_1} (${item.code_1})`,
            value: item.description_1,
            code: item.code_1
          }));
        setScStandardOptions(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching SC standard values:', error);
      notify('Failed to load SC standard options', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Fetch withdrawal reason values from DTrackIt API
  const getWithdrawalReasonValues = async () => {
    try {
      setLoading(true);
      const response = await dtrackItServiceV4.getStandardWithdrawalReasonValues();
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: `${item.description_1} (${item.code_1})`,
          value: item.code_1,
          code: item.code_1
        }));
        setWithdrawalReasonOptions(transformedOptions);
      }
    } catch (error) {
      console.error('Error fetching withdrawal reason values:', error);
      notify('Failed to load withdrawal reason options', 'error');
    } finally {
      setLoading(false);
    }
  };

  const formik = useFormik<StandardDetails>({
    initialValues: {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scStandardAddedDate: "",
      scStandardRemovedDate: "",
      scStandardWithdrawalReason: [],
      scStandardWithdrawalReasonDescription: "",
    },
    validationSchema: validationSchema,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: () => {
      // Submission logic if needed
    },
  });

  useScrollToError(formik);

  const handleCellChange = (rowIndex: number, columnId: string, value: any) => {

    if(columnId === 'scStandardWithdrawalReason') {
      // Handle array of selected values for multi-select
      const selectedValues = Array.isArray(value) ? value : [];
      const selectedDescriptions = selectedValues.map(val => 
        withdrawalReasonOptions.find(option => option.value === val)?.label || val
      ).join(', ');
      
      formik.setFieldValue('scStandardWithdrawalReason', selectedValues);
      formik.setFieldValue('scStandardWithdrawalReasonDescription', selectedDescriptions);
        setData((prevData) =>
          prevData.map((row, index) =>
          index === rowIndex ? { ...row, ['scStandardWithdrawalReason']: selectedValues, 'scStandardWithdrawalReasonDescription': selectedDescriptions } : row
          )
        );
    } else if(columnId === 'scStandard') {
      // Handle scStandard selection - value should be a code from the table
      const selectedCode = value; // value is the code from the table
      const selectedDescription = String(scStandardOptions.find(option => option.code === value)?.value || "");
      
      formik.setFieldValue('scStandard', selectedCode);
      formik.setFieldValue('scStandardDescription', selectedDescription);
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, ['scStandard']: selectedCode, 'scStandardDescription': selectedDescription } : row
          )
        );
    } else if(columnId === 'scStandardRemovedDate') {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
      // Reset withdrawal reason if removed date is cleared
      if (!value || value.trim() === '') {
        formik.setFieldValue('scStandardWithdrawalReason', []);
        formik.setFieldValue('scStandardWithdrawalReasonDescription', '');
        setData((prevData) =>
          prevData.map((row, index) =>
            index === rowIndex ? { ...row, 'scStandardWithdrawalReason': [], 'scStandardWithdrawalReasonDescription': '' } : row
          )
        );
      }
    } else {
      formik.setFieldValue(columnId, value);
      setData((prevData) =>
        prevData.map((row, index) =>
          index === rowIndex ? { ...row, [columnId]: value } : row
        )
      );
    }
    
  };

  const handleAddNewRow = () => {
    if (isSuspended) {
      notify('Standard details cannot be modified when SC is Suspended. Only SC Status can be changed.', 'warning');
      return;
    }
    const defaultAddedDate = scValidFromDate || new Date().toISOString().split('T')[0];
    const newRow: StandardDetails = {
      scStandard: "",
      scStandardDescription: "",
      scStandardVersion: "",
      scStandardAddedDate: defaultAddedDate,
      scStandardRemovedDate: "",
      scStandardWithdrawalReason: [],
      scStandardWithdrawalReasonDescription: "",
      scStandardReAdded: false,
    };
    setRestrictPopUpSave(true);
    setData((prevData) => [...prevData, newRow]);
    setEditingRowIndex(data.length);
    formik.setValues(newRow);
    formik.setTouched({});
  };

  const handleCancelEdit = async () => {
    // const currentRowValues = formik.values;

    // // Determine if the row being edited is new by comparing `editingRowIndex` with the data length
    // const isNewRow = editingRowIndex === data.length - 1;

    // try {
    //   // Validate the row using the Formik validation schema
    //   await validationSchema.validate(currentRowValues, { abortEarly: false });

    //   if (isNewRow) {
    //     // If validation passes and it's a new row, keep it in the data
    //     setData((prevData) => prevData.map((row, index) => (index === editingRowIndex ? currentRowValues : row)));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // } catch (validationErrors) {
    //   if (isNewRow) {
    //     // If validation fails and it's a new row, remove it from the data
    //     setData((prevData) => prevData.slice(0, -1));
    //   } else {
    //     // If it's an existing row, revert changes
    //     formik.setValues(data[editingRowIndex]);
    //   }
    // }
    setRestrictPopUpSave(false);
    setData(tableData);
    // Reset editing state and form
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  const handleSaveRow = async () => {
    if (isSuspended) {
      notify('Standard details cannot be modified when SC is Suspended. Only SC Status can be changed.', 'warning');
      return;
    }
    if (editingRowIndex === null) {
      notify('No row is being edited', 'error');
      return;
    }

    // Validate the current row data
    const currentRowData = data[editingRowIndex];
    const hasRemovedDate = currentRowData?.scStandardRemovedDate && currentRowData.scStandardRemovedDate.trim() !== '';
    const hasWithdrawalReason = currentRowData?.scStandardWithdrawalReason && Array.isArray(currentRowData.scStandardWithdrawalReason) && currentRowData.scStandardWithdrawalReason.length > 0;
    
    // Check if withdrawal reason is required but missing (B7: removedDate → reason required)
    if (hasRemovedDate && !hasWithdrawalReason) {
      notify('SC Standard Withdrawal Reason is required when SC Standard Removed Date is filled', 'error');
      return;
    }
    // B7 reverse: reason filled without a removed date
    if (hasWithdrawalReason && !hasRemovedDate) {
      notify('SC Standard Removed Date is required when SC Standard Withdrawal Reason is selected', 'error');
      return;
    }

    // Withdrawn SC enforcement: ALL rows must have removed date + withdrawal reason
    if (scStatus === 'W' || !!scWithdrawalDate) {
      if (!hasRemovedDate) {
        notify('SC Standard Removed Date is required for all standards when SC is Withdrawn', 'error');
        return;
      }
      if (!hasWithdrawalReason) {
        notify('SC Standard Withdrawal Reason is required for all standards when SC is Withdrawn', 'error');
        return;
      }
    }

    // Validate formik form explicitly to avoid stale isValid
    const validationErrors = await formik.validateForm();
    if (Object.keys(validationErrors).length > 0) {
      // Mark fields as touched so inline errors show
      const touchedMap = Object.keys(validationErrors).reduce((acc, key) => ({ ...acc, [key]: true }), {});
      formik.setTouched(touchedMap);
      
      notify('Please fix the highlighted errors', 'error');
      
      // Increment submitCount to trigger useScrollToError
      formik.submitForm();
      return;
    }

    // Check for duplicate standard
    const selectedStandard = formik.values.scStandard;
    const isDuplicate = data.some((row, index) => index !== editingRowIndex && row.scStandard === selectedStandard);
    if (isDuplicate) {
      formik.setFieldTouched('scStandard', true, false);
      notify(`Standard ${selectedStandard} is already added in another row. Duplicate standards are not allowed.`, 'error');
      return;
    }

    // Compute scStandardReAdded flag: true when addedDate > removedDate and removedDate is not empty
    const addedDate = formik.values.scStandardAddedDate;
    const removedDate = formik.values.scStandardRemovedDate;
    const scStandardReAdded = !!(
      removedDate &&
      removedDate.trim() !== '' &&
      addedDate &&
      new Date(addedDate) > new Date(removedDate)
    );

    const updatedRow: StandardDetails = { ...formik.values, scStandardReAdded };
    const newData = data.map((row, index) =>
        index === editingRowIndex ? updatedRow : row
      );
    setData(newData);
    setTableData(newData);

    const allStandards = newData.map(row => row.scStandard);
    setScStandards(allStandards);
    // Keep description map in sync
    const descMap: Record<string, string> = {};
    newData.forEach(row => { if (row.scStandard && row.scStandardDescription) descMap[row.scStandard] = row.scStandardDescription; });
    setScStandardDescriptions(descMap);
    setRestrictPopUpSave(false);
    setStandardData(data); // Update parent state
    setEditingRowIndex(-1);
    formik.resetForm();
  };

  useEffect(() => {
    const currentStore = useScStore.getState();
    
    // Reset the restrict popup save flag on component mount
    setRestrictPopUpSave(false);
    
    // Fetch SC standard values and withdrawal reason values from DTrackIt API
    getScStandardValues();
    getWithdrawalReasonValues();
    // Clear version options on mount
    setStandardVersionOptions([]);

    return (()=>{
      setRestrictPopUpSave(false);
    })
  }, []);

  // Fetch standard version values from DTrackIt API based on selected standard
  const getStandardVersions = async (standard: string) => {
    try {
      if (!standard) {
        setStandardVersionOptions([]);
        return;
      }
      const response = await dtrackItServiceV4.getStandardVersions(standard);
      if (response?.data) {
        const transformedOptions = response.data.map((item: any) => ({
          label: `${item.description_1}`,
          value: item.code_1,
          code: item.code_1
        }));
        setStandardVersionOptions(transformedOptions);
      } else {
        setStandardVersionOptions([]);
      }
    } catch (error) {
      console.error('Error fetching standard versions:', error);
      notify('Failed to load standard version options', 'error');
      setStandardVersionOptions([]);
    }
  };

  const columns: EditableColumnDef<StandardDetails>[] = [
    {
      accessorKey: "scStandard",
      header: "SC Standard",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard</span>,
      size: 200,
      minSize: 150,
      maxSize: 300,
      renderEditCell: ({ value, onChange }) => {
        // Handle both cases: value could be a code or a description
        let currentDescription: string = "";
        let currentCode = value;
        
        // Check if value is a description (by matching with option.value)
        const descriptionMatch = scStandardOptions.find(option => option.value === value);
        if (descriptionMatch) {
          // It's a description, use it and find the code
          currentDescription = value;
          currentCode = descriptionMatch.code || "";
        } else {
          // It's a code, find the description
          currentDescription = String(scStandardOptions.find(option => option.code === value)?.value || "");
        }
        
        return (
          <CustomSelect
            name="scStandard"
            value={currentDescription}
            placeholder="Select SC Standard"
            options={scStandardOptions}
            disabled={isWithdrawn} // Lock standard if already withdrawn
            tooltipError={true}
            error={formik.touched.scStandard && Boolean(formik.errors.scStandard)}
            helperText={formik.touched.scStandard && formik.errors.scStandard ? (formik.errors.scStandard as string) : undefined}
            onChange={(e) => {
              // When user selects description, pass the code to the table
              const selectedCode = scStandardOptions.find(option => option.value === e.target.value)?.code || "";
              console.log('Standard selected:', { selectedValue: e.target.value, selectedCode });
              onChange(selectedCode);
              
              // Fetch versions for the selected standard
              if (selectedCode) {
                console.log('Calling getStandardVersions with:', selectedCode);
                getStandardVersions(selectedCode);
              } else {
                console.log('No selected code, clearing version options');
                setStandardVersionOptions([]);
              }
            }}
          />
        );
      },
    },
    {
      accessorKey: "scStandardVersion",
      header: "SC Standard Version",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard Version</span>,
      size: 180,
      minSize: 130,
      maxSize: 250,
      renderEditCell: ({ value, onChange, rowIndex }) => {
        // Check if a standard is selected in the same row - check both tableData and current editing row
        const tableStandard = tableData[rowIndex]?.scStandard;
        const editingStandard = editingRowIndex === rowIndex ? formik.values.scStandard : null;
        const selectedStandard = editingStandard || tableStandard;

        // Disable ONLY when no standard has been selected yet.
        // Do NOT disable when standardVersionOptions is empty — some standards (e.g. MMS)
        // may have no versions returned by the API, but the dropdown must still be enabled
        // so the user can interact with it (or it shows "No options available").
        const isDisabled = !selectedStandard;

        const placeholder = !selectedStandard
          ? 'Select a standard first'
          : standardVersionOptions.length === 0
          ? 'No versions available'
          : 'Select SC Standard Version';

        return (
          <CustomSelect
            name="scStandardVersion"
            value={value || ""}
            placeholder={placeholder}
            options={standardVersionOptions}
            disabled={isDisabled || isWithdrawn} // Lock version if already withdrawn
            tooltipError={true}
            error={formik.touched.scStandardVersion && Boolean(formik.errors.scStandardVersion)}
            helperText={formik.touched.scStandardVersion && formik.errors.scStandardVersion ? (formik.errors.scStandardVersion as string) : undefined}
            onChange={(e) => onChange(e.target.value)}
          />
        );
      },
    },
    {
      accessorKey: "scStandardAddedDate",
      header: "SC Standard Added Date",
      Header: <span> <i style={{ color: 'red' }}>* </i>SC Standard Added Date</span>,
      size: 180,
      minSize: 150,
      maxSize: 250,
      renderEditCell: ({ value, onChange }) => (
          <CustomTextField
            name="scStandardAddedDate"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="Enter SC Standard Added Date"
            type="date"
            disabled={isWithdrawn} // Lock added date if already withdrawn
            error={formik.touched.scStandardAddedDate && Boolean(formik.errors.scStandardAddedDate)}
            helperText={formik.touched.scStandardAddedDate && formik.errors.scStandardAddedDate as string}
            inputProps={{ 
              min: scValidFromDate || undefined 
            }}
          />
      ),
    },
    {
      accessorKey: "scStandardRemovedDate",
      header: "SC Standard Removed Date",
      Header: scStatus === "W" ? <span> <i style={{ color: 'red' }}>* </i>SC Standard Removed Date</span> : "SC Standard Removed Date",
      size: 180,
      minSize: 150,
      maxSize: 250,
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomTextField
          name="scStandardRemovedDate"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Enter SC Standard Removed Date"
          type="date"
          error={formik.touched.scStandardRemovedDate && Boolean(formik.errors.scStandardRemovedDate)}
          helperText={formik.touched.scStandardRemovedDate && formik.errors.scStandardRemovedDate as string}
          // inputProps={{ min: data[rowIndex]?.scStandardAddedDate || undefined }}
        />
      ),
    },
    {
      accessorKey: "scStandardWithdrawalReason",
      header: "SC Standard Withdrawal Reason",
      Header: (scStatus === "W" || data.some(row => row.scStandardRemovedDate && row.scStandardRemovedDate.trim() !== '')) 
        ? <span> <i style={{ color: 'red' }}>* </i>SC Standard Withdrawal Reason</span> 
        : "SC Standard Withdrawal Reason",
      size: 250,
      minSize: 180,
      maxSize: 350,
      renderEditCell: ({ value, onChange, rowIndex }) => (
        <CustomSelect
          name="scStandardWithdrawalReason"
          value={value || []}
          placeholder="Select SC Standard Withdrawal Reason"
          options={withdrawalReasonOptions}
          multiple={true}
          disabled={!formik.values?.scStandardRemovedDate || formik.values?.scStandardRemovedDate.trim() === ''}
          tooltipError={true}
          error={formik.touched.scStandardWithdrawalReason && Boolean(formik.errors.scStandardWithdrawalReason)}
          helperText={formik.touched.scStandardWithdrawalReason && formik.errors.scStandardWithdrawalReason ? (formik.errors.scStandardWithdrawalReason as string) : undefined}
          onChange={(e) => {
            onChange(e.target.value)
          }}
        />
      ),
    },
  ];

  const handleEditRow = (rowIndex: number) => {
    setRestrictPopUpSave(true);
    setEditingRowIndex(rowIndex);
    formik.setValues(data[rowIndex]);
    
    // Fetch standard versions if a standard is already selected
    const selectedStandard = data[rowIndex]?.scStandard;
    if (selectedStandard) {
      console.log('Fetching versions for existing standard on edit:', selectedStandard);
      getStandardVersions(selectedStandard);
    } else {
      setStandardVersionOptions([]);
    }
  };

  const handleDeleteRow = (rowIndex: number) => {
    const updatedData = data.filter((_, index) => index !== rowIndex);
    setData(updatedData);
    setTableData(updatedData);
    setStandardData(updatedData); // Update parent with updated data
    const remainingStandards = updatedData.map(row => row.scStandard);

    setScStandards(remainingStandards);
    // Keep description map in sync
    const descMap: Record<string, string> = {};
    updatedData.forEach(row => { if (row.scStandard && row.scStandardDescription) descMap[row.scStandard] = row.scStandardDescription; });
    setScStandardDescriptions(descMap);
  };
//
  return (
    <div>
      <Box
        component="main"
        sx={{ flexGrow: 1, p: 1, my: 4 }}
      >
        <Box display="flex" mb={3} justifyContent="flex-end" width="100%">
          <StyledButton onClick={handleAddNewRow} sx={{ mb: 2 }} disabled={isEditingDisabled || isWithdrawn || isRestrictPopUpSave}>
            Add Standard Information
          </StyledButton>
        </Box>
        <EditableTable
          data={data}
          columns={columns}
          onSaveRow={handleSaveRow}
          onCellChange={handleCellChange}
          editingRowIndex={editingRowIndex}
          onEditRow={handleEditRow}
          onCancelEdit={handleCancelEdit}
          onDeleteRow={handleDeleteRow}
          enableEditing={!isEditingDisabled}
          hideDelete={isWithdrawn}
        />
      </Box>
    </div>
  );
};

export default StandardDetailsForm;
